import { useEffect } from 'react'
import { IS_ELECTRON } from '../services/config'
import { useLivekitStore } from '../stores/livekitStore'
import { APP_VERSION } from '../services/version'
import api from '../services/api'

// Desktop-only self-update. The Electron app loads the LIVE site, so a hard
// reload (== Ctrl+Shift+R) pulls the freshest deployed web bundle. A desktop
// that's left running for days would otherwise drift behind the web; this
// reloads it once a day at 01:00 local time so overnight machines come up
// current. A genuine cache-bypassing reload via the preload bridge, falling
// back to a plain reload on older shells that don't expose it.
const RELOAD_HOUR = 1 // 01:00 local
const MAX_ARM_MS = 6 * 60 * 60 * 1000 // re-evaluate at least every 6h (sleep/wake safe)
// OSPROD-270: catch-up reload for machines that are always asleep at 01:00
// (macOS laptops with the lid closed). If the page has been alive past this
// long, the 1am window was missed at least once — reload as soon as it's
// safe (window hidden, no active call, AND a newer build is actually
// deployed), instead of waiting for a 1am that never comes.
const STALE_PAGE_MS = 26 * 60 * 60 * 1000
const PAGE_LOADED_AT = Date.now()

function hardReload() {
  const api = window.electronAPI
  if (api?.hardReload) api.hardReload()
  else window.location.reload()
}

export default function ElectronDailyReload() {
  useEffect(() => {
    // Only when the desktop shell actually loads the LIVE site. An older shell
    // still on the baked file:// bundle can't pull a new build via reload, so a
    // daily reload there would just re-render the same stale bundle — skip it.
    if (!IS_ELECTRON || window.location.protocol === 'file:') return
    let timer: ReturnType<typeof setTimeout>

    const msUntilNext = () => {
      const now = new Date()
      const next = new Date(now)
      next.setHours(RELOAD_HOUR, 0, 0, 0)
      if (next.getTime() <= now.getTime()) next.setDate(next.getDate() + 1)
      return next.getTime() - now.getTime()
    }
    // Cap each timer so a machine that slept through the target wakes and
    // re-checks the wall clock instead of firing hours late.
    const arm = (ms: number) => { timer = setTimeout(tick, Math.min(ms, MAX_ARM_MS)) }
    const tick = () => {
      if (new Date().getHours() === RELOAD_HOUR) {
        // Never yank an active call out from under someone — retry in 30 min.
        if (useLivekitStore.getState().room) { arm(30 * 60 * 1000); return }
        hardReload()
        return
      }
      // Missed-1am catch-up (OSPROD-270): only while the window is hidden
      // (user isn't looking), never during a call, and only when the server
      // actually runs a different build — a reload that lands the same
      // bundle is pure disruption.
      if (Date.now() - PAGE_LOADED_AT > STALE_PAGE_MS) {
        if (document.hidden && !useLivekitStore.getState().room) {
          api.get<{ version: string }>('/version').then((r) => {
            const remote = r.data?.version
            if (typeof remote === 'string' && remote && remote !== 'unknown' && remote !== APP_VERSION
              && document.hidden
              && !useLivekitStore.getState().room) {
              hardReload()
            }
          }).catch(() => { /* offline — next tick retries */ })
        }
        // Overdue: poll every 30 min for the next safe (hidden, no-call) moment.
        arm(30 * 60 * 1000)
        return
      }
      arm(msUntilNext())
    }

    arm(msUntilNext())
    return () => clearTimeout(timer)
  }, [])

  return null
}
